/**
 * 3D view of a site's network plant: each room is a floor plate, each rack a
 * frame whose units are coloured by survey confidence, and every recorded link
 * an arc between the two ends. Clicking a unit or a rack selects it; the camera
 * flies to the selected rack, and its links (or a queried path) light up.
 * Choosing a network flies to the racks that hold it, fades everything else
 * back, and floats a label beside each member device (name, IP, MAC, where it
 * lives), laid out by `lib/calloutLayout.ts` and re-projected every frame.
 *
 * three.js is loaded lazily, as in the customer portal's PropertyGlobe, so the
 * rest of the admin bundle does not pay for it. Colours come from the semantic
 * tokens at build time and the scene rebuilds when the theme flips.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type * as THREE_NS from 'three';
import type { OrbitControls as OrbitControlsT } from 'three/examples/jsm/controls/OrbitControls.js';
import { kindToken } from '../../lib/deviceColors';
import { layoutCallouts, type CalloutAnchor } from '../../lib/calloutLayout';
import {
  CONFIDENCE_LABEL,
  deviceCallout,
  networkCalloutUnits,
  networkColor,
  networkFootprint,
  ruLabel,
  unitBottomOffset,
  type DeviceCallout,
  type RackPlant,
  type RackUnitRow,
} from '../../lib/rackModel';

type THREE = typeof THREE_NS;

export interface RackSceneProps {
  plant: RackPlant;
  selectedRackId: string | null;
  selectedUnitId: string | null;
  /** Links to emphasise (a queried path). Falls back to the selection's links. */
  highlightLinkIds?: string[];
  /** Network to emphasise: its devices glow, rooms it is in get a tinted floor. */
  networkId?: string | null;
  /** Bump to fly back to the whole-site view. */
  overviewNonce?: number;
  /** Pixels on the right covered by an overlay (the full-screen panel). */
  rightInset?: number;
  onSelectRack: (rackId: string) => void;
  onSelectUnit: (unitId: string) => void;
}

// Scene units: 1 = one rack unit (44.45 mm). A 19" rack is ~11 RU wide.
const RACK_W = 11;
const RACK_D = 12;
const UNIT_W = 10;
const FRONT_DEPTH = 7;
const REAR_DEPTH = 2.5;
const RACK_GAP = 4;
const ROOM_PAD = 5;
const ROOM_GAP = 10;

// Floating device labels (px). A label shows only what is recorded: name and
// where always; role, IP and MAC when known. Full height grows by one line per
// optional fact the fullest label carries; compact is the name plus whichever
// addresses exist.
const CALLOUT = { width: 236, gap: 8, margin: 56 };
const CALLOUT_LINE = 15;
const CALLOUT_BASE_FULL = 46;
const CALLOUT_BASE_COMPACT = 32;

/** One label's heights: one line per optional fact it actually carries. */
function calloutHeights(c: DeviceCallout) {
  return {
    full: CALLOUT_BASE_FULL + (Number(!!c.role) + Number(!!c.ip) + Number(!!c.mac)) * CALLOUT_LINE,
    compact: CALLOUT_BASE_COMPACT + (c.ip || c.mac ? CALLOUT_LINE - 1 : 0),
  };
}
const CALLOUT_SIZES = { ...CALLOUT, full: CALLOUT_BASE_FULL, compact: CALLOUT_BASE_COMPACT };
const CALLOUT_TOP = 56;
/** Where a leader meets its device: this share of the width in from the near side. */
const ANCHOR_INSET = 1 / 3;
const CALLOUT_BOTTOM = 44;

interface SceneState {
  THREE: THREE;
  camera: THREE_NS.PerspectiveCamera;
  controls: OrbitControlsT;
  unitMeshes: Map<string, THREE_NS.Mesh>;
  linkMeshes: Map<string, THREE_NS.Mesh>;
  rackBoxes: Map<string, { center: THREE_NS.Vector3; height: number }>;
  floors: Map<string, THREE_NS.Mesh>;
  rackLabels: Map<string, THREE_NS.Sprite>;
  roomLabels: Map<string, THREE_NS.Sprite>;
  frameMat: THREE_NS.MeshStandardMaterial;
  flagMat: THREE_NS.MeshStandardMaterial;
  flying: () => boolean;
  overview: { target: THREE_NS.Vector3; position: THREE_NS.Vector3 };
  fly: (target: THREE_NS.Vector3, position: THREE_NS.Vector3) => void;
  colors: Palette;
}

interface Palette {
  verified: string; deduced: string; contested: string; unknown: string;
  frame: string; floor: string; floorEdge: string; text: string; muted: string;
  fiber: string; copper: string; logical: string; flag: string; select: string; bg: string;
}

function readPalette(): Palette {
  const css = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  return {
    verified: v('--success', '#437A22'),
    deduced: v('--success-soft', '#D4DFCC'),
    contested: v('--warning', '#964219'),
    unknown: v('--warning-soft', '#DDCFC6'),
    frame: v('--text-muted', '#7A7974'),
    floor: v('--surface-2', '#F3F0EC'),
    floorEdge: v('--border', '#D4D1CA'),
    text: v('--text', '#28251D'),
    muted: v('--text-muted', '#7A7974'),
    fiber: v('--accent-orange', '#DA7101'),
    copper: v('--info', '#006494'),
    logical: v('--accent-purple', '#7A39BB'),
    flag: v('--accent-gold', '#D19900'),
    select: v('--primary', '#01696F'),
    bg: v('--bg', '#F7F6F2'),
  };
}

function cssVar(name: string, fallback: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function useThemeKey(): string {
  const [key, setKey] = useState(() => document.documentElement.dataset.theme ?? 'light');
  useEffect(() => {
    const obs = new MutationObserver(() => setKey(document.documentElement.dataset.theme ?? 'light'));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-brand'] });
    return () => obs.disconnect();
  }, []);
  return key;
}

function textSprite(THREE: THREE, text: string, color: string, size: number): THREE_NS.Sprite {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const font = `600 64px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(text).width) + 24;
  canvas.width = w;
  canvas.height = 88;
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 12, 46);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
  sprite.scale.set((w / 88) * size, size, 1);
  return sprite;
}

export function RackScene3D(props: RackSceneProps) {
  const { plant, selectedRackId, selectedUnitId, highlightLinkIds, networkId, overviewNonce, rightInset = 0, onSelectRack, onSelectUnit } = props;
  const hostRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<SceneState | null>(null);
  const callbacks = useRef({ onSelectRack, onSelectUnit });
  callbacks.current = { onSelectRack, onSelectUnit };
  const themeKey = useThemeKey();
  const [hover, setHover] = useState<{ x: number; y: number; text: string; sub: string } | null>(null);
  const [failed, setFailed] = useState(false);
  // Called by the render loop after each frame; positions the device labels.
  const frameHook = useRef<(() => void) | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  const lineRefs = useRef(new Map<string, SVGGElement>());
  const [compact, setCompact] = useState(false);
  const compactRef = useRef(compact);

  const network = networkId ? plant.networkById.get(networkId) : undefined;
  const calloutIds = useMemo(() => (networkId ? networkCalloutUnits(plant, networkId) : []), [plant, networkId]);
  const callouts = useMemo(
    () => calloutIds.map((id) => deviceCallout(plant, id)).filter((c): c is DeviceCallout => c !== null),
    [plant, calloutIds],
  );
  const heights = useMemo(() => new Map(callouts.map((c) => [c.unitId, calloutHeights(c)])), [callouts]);

  // Project each labelled device to the screen, lay the labels out, and write
  // positions straight to the DOM (no React render per frame). Labels stay
  // hidden while the camera is in flight so they arrive with it.
  const anchorVec = useRef<THREE_NS.Vector3 | null>(null);
  frameHook.current = callouts.length
    ? () => {
        const s = stateRef.current;
        const host = hostRef.current;
        const layer = layerRef.current;
        if (!s || !host || !layer) return;
        if (s.flying()) {
          layer.style.opacity = '0';
          return;
        }
        layer.style.opacity = '1';
        const w = host.clientWidth;
        const h = host.clientHeight;
        const right = w - rightInset;
        const v = (anchorVec.current ??= new s.THREE.Vector3());
        const anchors: CalloutAnchor[] = [];
        const edges = new Map<string, { l: { x: number; y: number }; r: { x: number; y: number } }>();
        const toScreen = () => {
          v.project(s.camera);
          return { x: ((v.x + 1) / 2) * w, y: ((1 - v.y) / 2) * h, behind: v.z > 1 };
        };
        for (const c of callouts) {
          const mesh = s.unitMeshes.get(c.unitId);
          if (!mesh) continue;
          const { width: gw, depth } = (mesh.geometry as THREE_NS.BoxGeometry).parameters;
          mesh.getWorldPosition(v);
          // On the device's front face, just proud of it. The unit's side
          // edges sit behind the rack posts, so a leader aimed there lands on
          // the frame; aim a third of the way in from the near side instead.
          const cx = v.x, cy = v.y, cz = v.z + depth / 2 + 0.05;
          const inset = gw * (0.5 - ANCHOR_INSET);
          v.set(cx, cy, cz);
          const mid = toScreen();
          if (mid.behind || mid.x < 0 || mid.x > right || mid.y < 0 || mid.y > h) continue;
          v.set(cx - inset, cy, cz);
          const l = toScreen();
          v.set(cx + inset, cy, cz);
          const r = toScreen();
          anchors.push({ id: c.unitId, x: mid.x, y: mid.y, ...heights.get(c.unitId) });
          edges.set(c.unitId, { l, r });
        }
        const layout = layoutCallouts(
          anchors,
          { left: 12, top: CALLOUT_TOP, right: right - 12, bottom: h - CALLOUT_BOTTOM },
          CALLOUT_SIZES,
        );
        if (layout.compact !== compactRef.current) {
          compactRef.current = layout.compact;
          setCompact(layout.compact);
        }
        const placed = new Map(layout.placed.map((p) => [p.id, p]));
        for (const [id, card] of cardRefs.current) {
          const p = placed.get(id);
          const line = lineRefs.current.get(id);
          if (!p) {
            card.style.visibility = 'hidden';
            line?.setAttribute('visibility', 'hidden');
            continue;
          }
          card.style.visibility = 'visible';
          card.style.transform = `translate(${Math.round(p.x)}px, ${Math.round(p.y)}px)`;
          card.style.height = `${p.height}px`;
          if (!line) continue;
          const e = edges.get(id)!;
          const end = p.side === 'left' ? e.l : e.r;
          line.setAttribute('visibility', 'visible');
          const [path, dot] = line.children as unknown as [SVGPathElement, SVGCircleElement];
          // Horizontal stub off the label, then straight to the device.
          const stub = p.side === 'left' ? p.leader.x1 + 14 : p.leader.x1 - 14;
          path.setAttribute('d', `M${p.leader.x1},${p.leader.y1} H${stub} L${end.x},${end.y}`);
          dot.setAttribute('cx', String(end.x));
          dot.setAttribute('cy', String(end.y));
        }
      }
    : null;

  // Build the scene. Rebuilt when the plant or the theme changes.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    let cleanup = () => {};

    (async () => {
      let THREE: THREE;
      let OrbitControls: typeof OrbitControlsT;
      try {
        THREE = await import('three');
        ({ OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js'));
      } catch {
        setFailed(true);
        return;
      }
      if (disposed) return;

      const colors = readPalette();
      let renderer: THREE_NS.WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      } catch {
        setFailed(true);
        return;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(host.clientWidth, host.clientHeight);
      host.appendChild(renderer.domElement);
      renderer.domElement.style.display = 'block';

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, host.clientWidth / Math.max(host.clientHeight, 1), 0.5, 2000);
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.maxPolarAngle = Math.PI * 0.49;

      scene.add(new THREE.HemisphereLight(0xffffff, 0x888877, 1.6));
      const sun = new THREE.DirectionalLight(0xffffff, 1.4);
      sun.position.set(40, 90, 60);
      scene.add(sun);

      const unitMeshes = new Map<string, THREE_NS.Mesh>();
      const linkMeshes = new Map<string, THREE_NS.Mesh>();
      const rackBoxes = new Map<string, { center: THREE_NS.Vector3; height: number }>();
      const floors = new Map<string, THREE_NS.Mesh>();
      const rackLabels = new Map<string, THREE_NS.Sprite>();
      const roomLabels = new Map<string, THREE_NS.Sprite>();
      const rackOrigins = new Map<string, THREE_NS.Vector3>();
      const pickables: THREE_NS.Object3D[] = [];
      const disposables: { dispose: () => void }[] = [];
      const track = <T extends { dispose: () => void }>(x: T) => (disposables.push(x), x);

      const frameMat = track(new THREE.MeshStandardMaterial({ color: colors.frame, metalness: 0.4, roughness: 0.6 }));
      const floorMat = track(new THREE.MeshStandardMaterial({ color: colors.floor, roughness: 1 }));
      const flagMat = track(new THREE.MeshStandardMaterial({ color: colors.flag, emissive: colors.flag, emissiveIntensity: 0.4 }));
      const shellMat = track(new THREE.MeshBasicMaterial({ visible: false }));
      // Colour by device type; survey confidence rides on top of it: deduced
      // positions are translucent, contested / unknown get an amber outline.
      const css = getComputedStyle(document.documentElement);
      const kindColors = new Map<string, string>();
      const colorFor = (kind: string) => {
        const token = kindToken(kind);
        if (!kindColors.has(token)) kindColors.set(token, css.getPropertyValue(`--${token}`).trim() || colors.frame);
        return kindColors.get(token)!;
      };
      const baseOpacity = (u: RackUnitRow) => (u.confidence === 'deduced' ? 0.62 : 1);
      const doubtMat = track(new THREE.LineBasicMaterial({ color: colors.contested, transparent: true }));
      const unitMat = (u: RackUnitRow) =>
        track(new THREE.MeshStandardMaterial({
          color: colorFor(u.kind),
          roughness: u.kind === 'patch_panel' ? 0.8 : 0.5,
          metalness: u.kind === 'patch_panel' ? 0.05 : 0.2,
          emissive: colors.select,
          emissiveIntensity: 0,
          transparent: true,
          opacity: baseOpacity(u),
        }));

      // ── Layout: rooms left→right in survey order, racks side by side ──────
      let x = 0;
      for (const room of plant.rooms) {
        const racks = plant.racksByRoom.get(room.id) ?? [];
        if (!racks.length) continue;
        const roomW = racks.length * RACK_W + (racks.length - 1) * RACK_GAP + ROOM_PAD * 2;
        const roomD = RACK_D + ROOM_PAD * 2;
        const roomX = x + roomW / 2;

        const floor = new THREE.Mesh(track(new THREE.BoxGeometry(roomW, 0.4, roomD)), track(floorMat.clone()));
        floors.set(room.id, floor);
        floor.position.set(roomX, -0.2, 0);
        scene.add(floor);
        const edge = new THREE.LineSegments(
          track(new THREE.EdgesGeometry(floor.geometry)),
          track(new THREE.LineBasicMaterial({ color: colors.floorEdge })),
        );
        edge.position.copy(floor.position);
        scene.add(edge);

        const tallest = Math.max(...racks.map((r) => r.height_u));
        const roomLabel = textSprite(THREE, room.name ? `${room.code} · ${room.name}` : room.code, colors.text, 3.2);
        roomLabel.position.set(roomX, tallest + 7, 0);
        scene.add(roomLabel);
        roomLabels.set(room.id, roomLabel);

        racks.forEach((rack, i) => {
          const rx = x + ROOM_PAD + RACK_W / 2 + i * (RACK_W + RACK_GAP);
          const origin = new THREE.Vector3(rx, 0, 0);
          rackOrigins.set(rack.id, origin);
          const h = rack.height_u;
          rackBoxes.set(rack.id, { center: new THREE.Vector3(rx, h / 2, 0), height: h });

          const group = new THREE.Group();
          group.position.copy(origin);
          scene.add(group);

          // Frame: four posts, top and bottom plates.
          const post = track(new THREE.BoxGeometry(0.5, h, 0.5));
          for (const [px, pz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
            const m = new THREE.Mesh(post, frameMat);
            m.position.set((px * (RACK_W - 0.5)) / 2, h / 2, (pz * (RACK_D - 0.5)) / 2);
            group.add(m);
          }
          const plate = track(new THREE.BoxGeometry(RACK_W, 0.4, RACK_D));
          for (const py of [0.2, h + 0.2]) {
            const m = new THREE.Mesh(plate, frameMat);
            m.position.y = py;
            group.add(m);
          }
          if (!rack.height_captured) {
            const dash = new THREE.LineSegments(
              track(new THREE.EdgesGeometry(track(new THREE.BoxGeometry(RACK_W, 2, RACK_D)))),
              track(new THREE.LineDashedMaterial({ color: colors.muted, dashSize: 0.6, gapSize: 0.6 })),
            );
            dash.position.y = h + 1.4;
            dash.computeLineDistances();
            group.add(dash);
          }

          // Invisible shell makes the whole rack clickable.
          const shell = new THREE.Mesh(track(new THREE.BoxGeometry(RACK_W, h, RACK_D)), shellMat);
          shell.position.y = h / 2;
          shell.userData = { rackId: rack.id };
          group.add(shell);
          pickables.push(shell);

          const code = textSprite(THREE, rack.code, colors.muted, 2.2);
          code.position.set(0, h + 3, 0);
          group.add(code);
          rackLabels.set(rack.id, code);

          for (const u of plant.unitsByRack.get(rack.id) ?? []) {
            if (u.kind === 'vacant') continue;
            const depth = u.face === 'front' ? FRONT_DEPTH : REAR_DEPTH;
            const geo = track(new THREE.BoxGeometry(UNIT_W, Math.max(u.ru_height - 0.12, 0.4), depth));
            const mesh = new THREE.Mesh(geo, unitMat(u));
            const y = unitBottomOffset(rack, u) + u.ru_height / 2 + 0.4;
            const z = u.face === 'front' ? RACK_D / 2 - depth / 2 - 0.3 : -RACK_D / 2 + depth / 2 + 0.3;
            mesh.position.set(0, y, z);
            mesh.userData = { unitId: u.id, rackId: rack.id, baseOpacity: baseOpacity(u) };
            group.add(mesh);
            if (u.confidence === 'contested' || u.confidence === 'unknown') {
              const outline = new THREE.LineSegments(track(new THREE.EdgesGeometry(geo)), doubtMat);
              outline.scale.setScalar(1.02);
              mesh.add(outline);
            }
            unitMeshes.set(u.id, mesh);
            pickables.push(mesh);
            if (u.check_note) {
              const dot = new THREE.Mesh(track(new THREE.SphereGeometry(0.45, 12, 8)), flagMat);
              dot.position.set(UNIT_W / 2 + 0.9, y, RACK_D / 2 - 0.2);
              group.add(dot);
            }
          }
        });
        x += roomW + ROOM_GAP;
      }

      // ── Links: arcs above the racks ─────────────────────────────────────
      const endPoint = (rackId: string, unitId: string | null): THREE_NS.Vector3 | null => {
        const origin = rackOrigins.get(rackId);
        const rack = plant.rackById.get(rackId);
        if (!origin || !rack) return null;
        const u = unitId ? plant.unitById.get(unitId) : undefined;
        if (!u) return origin.clone().add(new THREE.Vector3(0, rack.height_u + 0.6, 0));
        const y = unitBottomOffset(rack, u) + u.ru_height / 2 + 0.4;
        const z = u.face === 'front' ? RACK_D / 2 : -RACK_D / 2;
        return origin.clone().add(new THREE.Vector3(UNIT_W / 2, y, z));
      };
      for (const link of plant.links) {
        const from = plant.unitById.get(link.from_unit_id)!;
        const toUnit = link.to_unit_id ? plant.unitById.get(link.to_unit_id) : undefined;
        const a = endPoint(from.rack_id, from.id);
        const b = endPoint(toUnit?.rack_id ?? link.to_rack_id!, toUnit?.id ?? null);
        if (!a || !b) continue;
        const same = (toUnit?.rack_id ?? link.to_rack_id) === from.rack_id;
        const lift = same ? 0 : Math.max(8, a.distanceTo(b) * 0.3);
        const top = Math.max(a.y, b.y) + lift;
        const c1 = same ? a.clone().add(new THREE.Vector3(5, 0, 2)) : new THREE.Vector3(a.x, top, a.z + 6);
        const c2 = same ? b.clone().add(new THREE.Vector3(5, 0, 2)) : new THREE.Vector3(b.x, top, b.z + 6);
        const curve = new THREE.CubicBezierCurve3(a, c1, c2, b);
        const color = link.medium.startsWith('fiber') ? colors.fiber : link.medium === 'copper' ? colors.copper : colors.logical;
        const mat = track(new THREE.MeshStandardMaterial({
          color, emissive: color, emissiveIntensity: 0.25, transparent: true, opacity: 0.85,
        }));
        const tube = new THREE.Mesh(track(new THREE.TubeGeometry(curve, 48, link.medium === 'logical' ? 0.12 : 0.2, 6, false)), mat);
        tube.userData = { linkId: link.id };
        scene.add(tube);
        linkMeshes.set(link.id, tube);
      }

      // ── Camera: frame everything ───────────────────────────────────────────
      const bounds = new THREE.Box3();
      for (const b of rackBoxes.values()) {
        bounds.expandByPoint(b.center.clone().add(new THREE.Vector3(-RACK_W, -b.height / 2, -RACK_D)));
        bounds.expandByPoint(b.center.clone().add(new THREE.Vector3(RACK_W, b.height / 2 + 8, RACK_D)));
      }
      const center = bounds.isEmpty() ? new THREE.Vector3() : bounds.getCenter(new THREE.Vector3());
      const size = bounds.isEmpty() ? 50 : bounds.getSize(new THREE.Vector3()).length();
      const overview = {
        target: center.clone(),
        position: center.clone().add(new THREE.Vector3(size * 0.08, size * 0.35, size * 0.85)),
      };
      camera.position.copy(overview.position);
      controls.target.copy(overview.target);
      controls.update();

      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      let flight: { t: number; fromT: THREE_NS.Vector3; toT: THREE_NS.Vector3; fromP: THREE_NS.Vector3; toP: THREE_NS.Vector3 } | null = null;
      const flying = () => flight !== null;
      const fly = (target: THREE_NS.Vector3, position: THREE_NS.Vector3) => {
        if (reduceMotion) {
          controls.target.copy(target);
          camera.position.copy(position);
          return;
        }
        flight = { t: 0, fromT: controls.target.clone(), toT: target, fromP: camera.position.clone(), toP: position };
      };

      // ── Picking and hover ──────────────────────────────────────────────────
      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      const pick = (ev: PointerEvent) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.set(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects(pickables, false);
        // Prefer a unit over the rack shell it sits inside.
        return hits.find((h) => h.object.userData.unitId) ?? hits[0];
      };
      let down: { x: number; y: number } | null = null;
      const onDown = (ev: PointerEvent) => { down = { x: ev.clientX, y: ev.clientY }; };
      const onUp = (ev: PointerEvent) => {
        if (!down || Math.hypot(ev.clientX - down.x, ev.clientY - down.y) > 5) return;
        down = null;
        const hit = pick(ev);
        if (!hit) return;
        if (hit.object.userData.unitId) callbacks.current.onSelectUnit(hit.object.userData.unitId);
        else if (hit.object.userData.rackId) callbacks.current.onSelectRack(hit.object.userData.rackId);
      };
      const onMove = (ev: PointerEvent) => {
        const hit = pick(ev);
        const unitId = hit?.object.userData.unitId as string | undefined;
        const u = unitId ? plant.unitById.get(unitId) : undefined;
        renderer.domElement.style.cursor = hit ? 'pointer' : 'grab';
        if (!u) return setHover(null);
        const rect = host.getBoundingClientRect();
        setHover({ x: ev.clientX - rect.left, y: ev.clientY - rect.top, text: u.label, sub: `${plant.rackById.get(u.rack_id)?.code} · ${ruLabel(u)}` });
      };
      const onLeave = () => setHover(null);
      renderer.domElement.addEventListener('pointerdown', onDown);
      renderer.domElement.addEventListener('pointerup', onUp);
      renderer.domElement.addEventListener('pointermove', onMove);
      renderer.domElement.addEventListener('pointerleave', onLeave);

      const resize = new ResizeObserver(() => {
        const w = host.clientWidth;
        const h = Math.max(host.clientHeight, 1);
        renderer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      });
      resize.observe(host);

      let frame = 0;
      const loop = () => {
        frame = requestAnimationFrame(loop);
        if (flight) {
          flight.t = Math.min(1, flight.t + 0.035);
          const e = 1 - Math.pow(1 - flight.t, 3);
          controls.target.lerpVectors(flight.fromT, flight.toT, e);
          camera.position.lerpVectors(flight.fromP, flight.toP, e);
          if (flight.t >= 1) flight = null;
        }
        controls.update();
        renderer.render(scene, camera);
        frameHook.current?.();
      };
      loop();

      stateRef.current = {
        THREE, camera, controls, unitMeshes, linkMeshes, rackBoxes, floors, rackLabels, roomLabels,
        frameMat, flagMat, flying, overview, fly, colors,
      };

      cleanup = () => {
        cancelAnimationFrame(frame);
        resize.disconnect();
        renderer.domElement.removeEventListener('pointerdown', onDown);
        renderer.domElement.removeEventListener('pointerup', onUp);
        renderer.domElement.removeEventListener('pointermove', onMove);
        renderer.domElement.removeEventListener('pointerleave', onLeave);
        controls.dispose();
        scene.traverse((o) => {
          if (o instanceof THREE.Sprite) {
            o.material.map?.dispose();
            o.material.dispose();
          }
        });
        disposables.forEach((d) => d.dispose());
        renderer.dispose();
        renderer.domElement.remove();
        stateRef.current = null;
      };
      // Apply the current selection to the fresh scene.
      applySelection();
    })();

    return () => {
      disposed = true;
      cleanup();
    };
    // applySelection reads props through refs; rebuild only on data or theme.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, themeKey]);

  const selectionRef = useRef({ selectedRackId, selectedUnitId, highlightLinkIds, networkId });
  selectionRef.current = { selectedRackId, selectedUnitId, highlightLinkIds, networkId };

  function applySelection() {
    const s = stateRef.current;
    if (!s) return;
    const { selectedUnitId: unitId, highlightLinkIds: pathIds, networkId: netId } = selectionRef.current;
    const rackId = selectionRef.current.selectedRackId;
    const fp = netId ? networkFootprint(plant, netId) : null;
    const netColor = fp ? new s.THREE.Color(cssVar(`--${fp.network.color_token}`, s.colors.select)) : null;

    const lit = new Set<string>(
      pathIds?.length ? pathIds
      : unitId ? (plant.linksByUnit.get(unitId) ?? []).map((e) => e.link.id)
      : fp ? fp.links.map((l) => l.id)
      : [],
    );
    for (const [roomId, floor] of s.floors) {
      const m = floor.material as THREE_NS.MeshStandardMaterial;
      m.color.set(fp && netColor && fp.roomIds.has(roomId) ? netColor.clone().lerp(new s.THREE.Color(s.colors.floor), 0.55) : s.colors.floor);
    }
    const litUnits = new Set<string>();
    for (const id of lit) {
      const l = plant.links.find((x) => x.id === id);
      if (l) {
        litUnits.add(l.from_unit_id);
        if (l.to_unit_id) litUnits.add(l.to_unit_id);
      }
    }
    // Interface lines stay hidden until a device, a path or a network calls
    // for them; then only those links draw.
    for (const [id, mesh] of s.linkMeshes) {
      const m = mesh.material as THREE_NS.MeshStandardMaterial;
      mesh.visible = lit.has(id);
      m.opacity = 1;
      m.emissiveIntensity = 0.7;
    }
    // A chosen network pushes everything that is not part of it far back:
    // other devices nearly vanish, frames and flags go faint, and only the
    // labels of racks and rooms that hold it stay legible.
    const focused = !!(fp && netColor);
    for (const m of [s.frameMat, s.flagMat]) {
      if (m.transparent !== focused) {
        m.transparent = focused;
        m.depthWrite = !focused;
        m.needsUpdate = true;
      }
      m.opacity = focused ? (m === s.frameMat ? 0.28 : 0.2) : 1;
    }
    const memberRacks = new Set(fp ? [...fp.unitIds].map((id) => plant.unitById.get(id)?.rack_id) : []);
    for (const [id, sprite] of s.rackLabels) sprite.material.opacity = !focused || memberRacks.has(id) ? 1 : 0.18;
    for (const [id, sprite] of s.roomLabels) sprite.material.opacity = !focused || fp!.roomIds.has(id) ? 1 : 0.18;
    for (const [id, mesh] of s.unitMeshes) {
      const m = mesh.material as THREE_NS.MeshStandardMaterial;
      if (fp && netColor) {
        const member = fp.unitIds.has(id);
        m.emissive.copy(id === unitId ? new s.THREE.Color(s.colors.select) : netColor);
        m.emissiveIntensity = id === unitId ? 0.85 : member ? 0.6 : 0;
        m.opacity = (member || id === unitId ? 1 : 0.05) * mesh.userData.baseOpacity;
        continue;
      }
      m.emissive.set(s.colors.select);
      const inRack = mesh.userData.rackId === rackId;
      m.emissiveIntensity = id === unitId ? 0.85 : litUnits.has(id) ? 0.35 : 0;
      m.opacity = (!rackId || inRack || litUnits.has(id) ? 1 : 0.45) * mesh.userData.baseOpacity;
    }
  }

  useEffect(() => {
    applySelection();
    const s = stateRef.current;
    if (!s || !selectedRackId) return;
    const box = s.rackBoxes.get(selectedRackId);
    if (!box) return;
    // Fit the rack (plus its label) to the vertical field of view, and to the
    // horizontal one on narrow viewports, then stand a little off-axis.
    const vHalf = (s.camera.fov * Math.PI) / 360;
    const hHalf = Math.atan(Math.tan(vHalf) * s.camera.aspect);
    const dist = Math.max((box.height * 0.62 + 4) / Math.tan(vHalf), (RACK_W * 1.6) / Math.tan(hHalf), 42);
    const target = box.center.clone().add(new s.THREE.Vector3(0, 1.5, 0));
    s.fly(target, target.clone().add(new s.THREE.Vector3(dist * 0.3, dist * 0.12, dist * 0.94)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRackId, selectedUnitId, highlightLinkIds]);

  // Choosing a network re-lights the scene and flies to the racks that hold
  // its devices, framed so the labels have room on both sides.
  useEffect(() => {
    applySelection();
    const s = stateRef.current;
    if (!s || !networkId || !calloutIds.length) return;
    const racks = new Set(calloutIds.map((id) => plant.unitById.get(id)?.rack_id));
    const box = new s.THREE.Box3();
    for (const id of racks) {
      const b = id ? s.rackBoxes.get(id) : undefined;
      if (!b) continue;
      box.expandByPoint(b.center.clone().add(new s.THREE.Vector3(-RACK_W / 2, -b.height / 2, 0)));
      box.expandByPoint(b.center.clone().add(new s.THREE.Vector3(RACK_W / 2, b.height / 2 + 4, 0)));
    }
    if (box.isEmpty()) return;
    const size = box.getSize(new s.THREE.Vector3());
    const host = hostRef.current;
    const width = host?.clientWidth ?? 1;
    // The racks get the width left after a label column on each side (and
    // never less than a third of the uncovered view).
    const visiblePx = Math.max(width - rightInset, 1);
    const racksPx = Math.max(visiblePx - 2 * (CALLOUT.width + CALLOUT.margin + 16), visiblePx / 3);
    const vHalf = (s.camera.fov * Math.PI) / 360;
    const hHalf = Math.atan(Math.tan(vHalf) * s.camera.aspect);
    const dist = Math.max((size.y * 0.58 + 3) / Math.tan(vHalf), size.x / 2 / (Math.tan(hHalf) * (racksPx / width)), 40);
    const dir = new s.THREE.Vector3(0.1, 0.2, 1).normalize();
    // Shift the aim right so the racks centre in the uncovered part of the view.
    const worldPerPx = (2 * dist * Math.tan(hHalf)) / width;
    const target = box.getCenter(new s.THREE.Vector3()).add(new s.THREE.Vector3((rightInset / 2) * worldPerPx, 0, 0));
    s.fly(target, target.clone().add(dir.multiplyScalar(dist)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, calloutIds]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s || !overviewNonce) return;
    s.fly(s.overview.target.clone(), s.overview.position.clone());
  }, [overviewNonce]);

  return (
    <div ref={hostRef} className="relative h-full w-full overflow-hidden" data-testid="rack-scene">
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-text-muted">
          3D view is unavailable in this browser (WebGL could not start). The position list and connections below still work.
        </div>
      )}
      {network && callouts.length > 0 && !failed && (
        <div
          ref={layerRef}
          className="pointer-events-none absolute inset-0 z-[5] transition-opacity duration-300"
          style={{ opacity: 0 }}
          data-testid="rack-callouts"
        >
          <svg className="absolute inset-0 h-full w-full" aria-hidden>
            {callouts.map((c) => (
              <g
                key={c.unitId}
                visibility="hidden"
                ref={(el) => {
                  if (el) lineRefs.current.set(c.unitId, el);
                  else lineRefs.current.delete(c.unitId);
                }}
              >
                <path fill="none" stroke={networkColor(network)} strokeWidth={1.25} strokeOpacity={0.9} />
                <circle r={3.5} fill={networkColor(network)} stroke="var(--bg-elevated)" strokeWidth={1.5} />
              </g>
            ))}
          </svg>
          {callouts.map((c) => (
            <div
              key={c.unitId}
              ref={(el) => {
                if (el) cardRefs.current.set(c.unitId, el);
                else cardRefs.current.delete(c.unitId);
              }}
              className="absolute left-0 top-0"
              style={{ width: CALLOUT.width, visibility: 'hidden' }}
            >
              <CalloutCard
                callout={c}
                color={networkColor(network)}
                compact={compact}
                selected={c.unitId === selectedUnitId}
                onSelect={() => onSelectUnit(c.unitId)}
              />
            </div>
          ))}
        </div>
      )}
      {hover && (
        <div
          className="pointer-events-none absolute z-10 max-w-xs rounded-sm border border-border bg-bg-elevated px-2 py-1 text-xs shadow"
          style={{ left: hover.x + 12, top: hover.y + 12 }}
        >
          <div className="font-medium text-text">{hover.text}</div>
          <div className="font-mono text-text-muted">{hover.sub}</div>
        </div>
      )}
    </div>
  );
}

/** One floating device label. Only recorded facts appear; nothing is guessed or placeheld. */
function CalloutCard({ callout: c, color, compact, selected, onSelect }: {
  callout: DeviceCallout;
  color: string;
  compact: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const doubt = c.confidence !== 'verified';
  // Only recorded addresses are shown; a label with neither has no address line.
  const addresses = [c.ip, c.mac].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      onClick={onSelect}
      title={`${c.title} — ${c.where}`}
      className={
        'pointer-events-auto flex h-full w-full flex-col gap-0.5 overflow-hidden rounded-md border bg-bg-elevated px-2.5 py-1.5 text-left shadow-lg ' +
        (selected ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-text-muted')
      }
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-[12.5px] font-semibold leading-tight text-text">{c.title}</span>
        {doubt && (
          <span className="flex-none rounded-sm border border-warning px-1 text-[9.5px] uppercase tracking-wide text-warning">
            {CONFIDENCE_LABEL[c.confidence]}
          </span>
        )}
      </span>
      {compact ? (
        addresses && <span className="truncate font-mono text-[10.5px] text-text-muted">{addresses}</span>
      ) : (
        <>
          {c.role && <span className="truncate text-[11px] leading-tight text-text-muted">{c.role}</span>}
          {(c.ip || c.mac) && (
            <span className="grid grid-cols-[2.4rem_1fr] font-mono text-[10.5px] leading-[1.35] text-text">
              {c.ip && (
                <>
                  <span className="text-text-muted">IP</span>
                  <span className="truncate">{c.ip}</span>
                </>
              )}
              {c.mac && (
                <>
                  <span className="text-text-muted">MAC</span>
                  <span className="truncate">{c.mac}</span>
                </>
              )}
            </span>
          )}
          <span className="truncate text-[11px] leading-tight text-text-muted">{c.where}</span>
        </>
      )}
    </button>
  );
}
